import { useState, useEffect } from 'react';

/**
 * Hook to detect user's motion preference
 * Returns true if user prefers reduced motion
 * 
 * @returns {boolean} prefersReducedMotion
 */
export const useReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if matchMedia is supported
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    // Set initial value
    setPrefersReducedMotion(mediaQuery?.matches);

    // Listen for changes
    const handleChange = (event) => {
      setPrefersReducedMotion(event?.matches);
    };

    // Add event listener (newer browsers)
    if (mediaQuery?.addEventListener) {
      mediaQuery?.addEventListener('change', handleChange);
      return () => mediaQuery?.removeEventListener('change', handleChange);
    } 
    // Fallback for older browsers
    else if (mediaQuery?.addListener) {
      mediaQuery?.addListener(handleChange);
      return () => mediaQuery?.removeListener(handleChange);
    }
  }, []);

  return prefersReducedMotion;
};

/**
 * Get appropriate animation config based on motion preference
 * 
 * @param {object} normalConfig - Animation config for normal motion
 * @param {object} reducedConfig - Animation config for reduced motion (optional)
 * @returns {object} Animation configuration
 */
export const useMotionConfig = (normalConfig, reducedConfig = { duration: 0.01 }) => {
  const prefersReducedMotion = useReducedMotion();
  return prefersReducedMotion ? reducedConfig : normalConfig;
};

export default useReducedMotion;